import React, { useState, useEffect } from "react";
import { Container, Table, Spinner, Alert, Form, Row, Col, Button, Badge, ButtonGroup, InputGroup } from "react-bootstrap";
import api from '../services/api'; 

const AllSurveys = () => {
  // State for data
  const [surveys, setSurveys] = useState([]);
  const [filteredSurveys, setFilteredSurveys] = useState([]);

  // State for loading and error handling
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for filter dropdown options
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);
  const [researchers, setResearchers] = useState([]);
  const [scriptingPersonnel, setScriptingPersonnel] = useState([]);
  const [dpPersonnel, setDpPersonnel] = useState([]);

  // State for selected filter values
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedResearcher, setSelectedResearcher] = useState("");
  const [selectedScripting, setSelectedScripting] = useState("");
  const [selectedDp, setSelectedDp] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  // State for actions
  const [resendingId, setResendingId] = useState(null);
  const [resendStatus, setResendStatus] = useState({ type: '', message: '' });
  const [copySuccessText, setCopySuccessText] = useState('');

  const getSurveyStatus = (survey) => {
    const allAnswered = 
      survey.Q1 && survey.Q2 && survey.Q3 && survey.Q4 && 
      survey.Q5 && survey.Q6 && survey.Q7 && survey.Q8;
    return allAnswered ? "Done" : "Not Done";
  };

  useEffect(() => {
    const fetchAllSurveys = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("Token");
        const response = await api.get('survey/download/', {
          headers: { Authorization: `Token ${token}` },
        });

        if (response.data && response.status !== 204) {
          setSurveys(response.data);
          setFilteredSurveys(response.data);
        } else {
          setSurveys([]);
          setFilteredSurveys([]);
        }
      } catch (err) {
        setError("Failed to fetch survey records. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchAllSurveys();
  }, []);

  useEffect(() => {
    if (surveys.length > 0) {
      setYears([...new Set(surveys.map(s => s.year))].sort((a, b) => b - a));
      setMonths([...new Set(surveys.map(s => s.month))]);
      setResearchers([...new Set(surveys.map(s => s.researcher_in_charge).filter(Boolean))].sort());
      setScriptingPersonnel([...new Set(surveys.map(s => s.scripting_in_charge).filter(Boolean))].sort());
      setDpPersonnel([...new Set(surveys.map(s => s.dp_in_charge).filter(Boolean))].sort());
    }
  }, [surveys]);

  useEffect(() => {
    let result = surveys;
    if (selectedYear) result = result.filter(s => s.year.toString() === selectedYear);
    if (selectedMonth) result = result.filter(s => s.month === selectedMonth);
    if (selectedResearcher) result = result.filter(s => s.researcher_in_charge === selectedResearcher);
    if (selectedScripting) result = result.filter(s => s.scripting_in_charge === selectedScripting);
    if (selectedDp) result = result.filter(s => s.dp_in_charge === selectedDp);
    if (selectedStatus) result = result.filter(s => getSurveyStatus(s) === selectedStatus);
    setFilteredSurveys(result);
  }, [selectedYear, selectedMonth, selectedResearcher, selectedScripting, selectedDp, selectedStatus, surveys]);

  const handleClearFilters = () => {
    setSelectedYear(""); setSelectedMonth(""); setSelectedResearcher("");
    setSelectedScripting(""); setSelectedDp(""); setSelectedStatus("");
  };

  const handleCopyLink = (surveyId) => {
    const surveyLink = `${window.location.origin}/satisfactionsurvey/${surveyId}`;
    navigator.clipboard.writeText(surveyLink);
    setCopySuccessText('Link copied!');
    setTimeout(() => setCopySuccessText(''), 2000);
  };
  
  const handleResendLink = async (surveyId) => {
    setResendingId(surveyId);
    setResendStatus({ type: '', message: '' });
    setCopySuccessText('');

    try {
      const token = localStorage.getItem("Token");
      const res = await api.post('survey/resend-reminder/', { survey_id: surveyId }, { headers: { Authorization: `Token ${token}` } });
      const newCount = res.data?.resend_count;
      if (newCount !== undefined) {
        setSurveys(prev => prev.map(s => s.survey_id === surveyId ? { ...s, resend_count: newCount } : s));
      }
      setResendStatus({ type: 'success', message: `Reminder successfully sent.` });
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to send reminder.';
      const surveyLink = `${window.location.origin}/satisfactionsurvey/${surveyId}`;

      const manualSendJsx = (
        <div>
          <p className="mb-2"><strong>{errorMessage}</strong></p>
          <p>You can copy the link and send it manually:</p>
          <InputGroup>
            <Form.Control value={surveyLink} readOnly />
            <Button variant="outline-secondary" onClick={() => handleCopyLink(surveyId)}>Copy</Button>
          </InputGroup>
          {copySuccessText && <small className="text-success d-block mt-1">{copySuccessText}</small>}
        </div>
      );
      
      setResendStatus({ type: 'danger', message: manualSendJsx });
    } finally {
      setResendingId(null);
    }
  };

  if (loading) return <Container className="text-center mt-5"><Spinner animation="border" /> <p>Loading...</p></Container>;
  if (error) return <Container className="mt-5"><Alert variant="danger">{error}</Alert></Container>;

  return (
    <Container fluid className="mt-5">
      <h2 className="text-center mb-4">All Survey Records</h2>

      <Row className="mb-4 p-3 border rounded bg-light align-items-end">
        <Col xl={2} md={4} sm={6} className="mb-2"><Form.Group><Form.Label>Year</Form.Label><Form.Control as="select" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}><option value="">All</option>{years.map(y => <option key={y} value={y}>{y}</option>)}</Form.Control></Form.Group></Col>
        <Col xl={2} md={4} sm={6} className="mb-2"><Form.Group><Form.Label>Month</Form.Label><Form.Control as="select" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}><option value="">All</option>{months.map(m => <option key={m} value={m}>{m}</option>)}</Form.Control></Form.Group></Col>
        <Col xl={2} md={4} sm={6} className="mb-2"><Form.Group><Form.Label>Researcher</Form.Label><Form.Control as="select" value={selectedResearcher} onChange={e => setSelectedResearcher(e.target.value)}><option value="">All</option>{researchers.map(r => <option key={r} value={r}>{r}</option>)}</Form.Control></Form.Group></Col>
        <Col xl={2} md={4} sm={6} className="mb-2"><Form.Group><Form.Label>Scripting</Form.Label><Form.Control as="select" value={selectedScripting} onChange={e => setSelectedScripting(e.target.value)}><option value="">All</option>{scriptingPersonnel.map(s => <option key={s} value={s}>{s}</option>)}</Form.Control></Form.Group></Col>
        <Col xl={2} md={4} sm={6} className="mb-2"><Form.Group><Form.Label>DP</Form.Label><Form.Control as="select" value={selectedDp} onChange={e => setSelectedDp(e.target.value)}><option value="">All</option>{dpPersonnel.map(d => <option key={d} value={d}>{d}</option>)}</Form.Control></Form.Group></Col>
        <Col xl={1} md={4} sm={6} className="mb-2"><Form.Group><Form.Label>Status</Form.Label><Form.Control as="select" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}><option value="">All</option><option value="Done">Done</option><option value="Not Done">Not Done</option></Form.Control></Form.Group></Col>
        <Col xl={1} md={12} sm={12} className="mb-2 d-grid"><Button variant="secondary" onClick={handleClearFilters}>Clear</Button></Col>
      </Row>

      {resendStatus.message && (
        <Alert variant={resendStatus.type} onClose={() => setResendStatus({ type: '', message: '' })} dismissible>
          {resendStatus.message}
        </Alert>
      )}

      {/* +++ START OF FIX: Display filtered record count +++ */}
      {!loading && surveys.length > 0 && (
        <div className="mb-3 text-end">
          <span className="fw-bold">{filteredSurveys.length}</span>
          <span className="text-muted ms-1">
            {filteredSurveys.length === 1 ? 'record found' : 'records found'}
          </span>
        </div>
      )}
      {/* +++ END OF FIX +++ */}
      
      {filteredSurveys.length === 0 && !loading ? (
        <Alert variant="info">No survey records match the current filters.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>#</th><th>Project Name</th><th>Job Number</th><th>Researcher</th><th>Year</th><th>Month</th><th>Type</th><th>Scripting</th><th>DP</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSurveys.map((survey, index) => {
              const status = getSurveyStatus(survey);
              return (
                <tr key={survey.survey_id}>
                  <td>{index + 1}</td>
                  <td>{survey.project_name}</td><td>{survey.job_number}</td><td>{survey.researcher_in_charge}</td>
                  <td>{survey.year}</td><td>{survey.month}</td><td>{survey.dp_or_scripting}</td>
                  <td>{survey.scripting_in_charge || 'N/A'}</td><td>{survey.dp_in_charge || 'N/A'}</td>
                  <td><Badge bg={status === "Done" ? "success" : "secondary"}>{status}</Badge></td>
                  <td>
                    {status === 'Not Done' && (
                      <ButtonGroup size="sm">
                        <Button
                          variant="outline-primary"
                          onClick={() => handleResendLink(survey.survey_id)}
                          disabled={resendingId === survey.survey_id}
                          title="Attempt to send reminder email automatically"
                        >
                          {resendingId === survey.survey_id
                            ? <Spinner as="span" animation="border" size="sm" />
                            : survey.resend_count > 0 ? `Resend (${survey.resend_count})` : 'Resend'}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={() => handleCopyLink(survey.survey_id)}
                          title="Copy survey link to clipboard"
                        >
                          Copy Link
                        </Button>
                      </ButtonGroup>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default AllSurveys;